import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash, ShoppingBag, ArrowRight, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { PREORDER_MODE } from "@/lib/siteConfig";
import { fbTrack } from "@/lib/fbpixel";

export default function Cart() {
  const { items, removeItem, setQty, total } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [shopify, setShopify] = useState({ connected: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/shopify/status").then(({ data }) => setShopify(data)).catch(() => {});
  }, []);

  const goCheckout = async () => {
    if (!user) return navigate("/auth");
    fbTrack("InitiateCheckout", { value: total, currency: "USD", content_type: "product", content_ids: items.map((i) => i.product_id), num_items: items.reduce((n, i) => n + i.quantity, 0) });
    if (shopify.connected) {
      const missing = items.filter((i) => !i.shopify_variant_id);
      if (missing.length === 0) {
        setBusy(true);
        try {
          const lineItems = items.map((i) => ({ variant_id: i.shopify_variant_id, quantity: i.quantity }));
          const { data } = await api.post("/shopify/checkout", { items: lineItems });
          window.location.href = data.checkout_url;
          return;
        } catch {
          toast.error("Shopify checkout unavailable — using standard checkout");
          setBusy(false);
        }
      } else {
        toast.message("Some items aren't mapped to Shopify yet — using standard checkout");
      }
    }
    navigate("/checkout");
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag size={56} className="mx-auto text-muted-foreground mb-6" />
        <h1 className="text-3xl font-modern font-light mb-3">Your bag is empty</h1>
        <p className="text-muted-foreground mb-8">Explore the Shop and add your first Wigglez piece.</p>
        <Link to="/shop" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3.5 rounded-full font-modern font-medium hover:bg-primary/90 transition-colors">
          Go to Shop <ArrowRight size={18} />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14" data-testid="cart-page">
      <h1 className="text-4xl tracking-tighter font-light mb-8">Your Bag</h1>
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4">
          {items.map((it) => (
            <div key={it.product_id} className="flex gap-4 p-4 rounded-xl bg-card border border-white/5" data-testid={`cart-item-${it.product_id}`}>
              <div className="h-24 w-20 rounded-lg overflow-hidden bg-muted shrink-0">
                {it.image && <img src={it.image} alt={it.name} className="h-full w-full object-cover" />}
              </div>
              <div className="flex-1">
                <p className="font-modern font-medium">{it.name}</p>
                <p className="text-primary mt-1">${it.price.toLocaleString()}</p>
                <div className="flex items-center gap-3 mt-3">
                  <div className="flex items-center border border-white/15 rounded-full text-sm">
                    <button onClick={() => setQty(it.product_id, it.quantity - 1)} className="px-3 py-1.5 hover:text-primary" data-testid={`cart-dec-${it.product_id}`}>−</button>
                    <span className="w-8 text-center">{it.quantity}</span>
                    <button onClick={() => setQty(it.product_id, it.quantity + 1)} className="px-3 py-1.5 hover:text-primary" data-testid={`cart-inc-${it.product_id}`}>+</button>
                  </div>
                  <button onClick={() => removeItem(it.product_id)} className="text-muted-foreground hover:text-accent transition-colors" data-testid={`cart-remove-${it.product_id}`}>
                    <Trash size={18} />
                  </button>
                </div>
              </div>
              <p className="font-modern">${(it.price * it.quantity).toLocaleString()}</p>
            </div>
          ))}
        </div>

        <div className="h-fit p-6 rounded-xl bg-card border border-white/10" data-testid="cart-summary">
          <h2 className="font-modern text-lg font-medium mb-5">Order summary</h2>
          <div className="flex justify-between text-sm text-muted-foreground mb-2"><span>Subtotal</span><span>${total.toLocaleString()}</span></div>
          <div className="flex justify-between text-sm text-muted-foreground mb-4"><span>Shipping</span><span>Calculated at checkout</span></div>
          <div className="h-px bg-white/10 my-4" />
          <div className="flex justify-between text-lg font-modern mb-6"><span>Total</span><span className="text-primary">${total.toLocaleString()}</span></div>
          <button onClick={goCheckout} disabled={busy} className="w-full bg-primary text-primary-foreground py-3.5 rounded-full font-modern font-medium hover:bg-primary/90 transition-colors disabled:opacity-60" data-testid="cart-checkout">
            {busy ? "Redirecting…" : !user ? "Sign in to checkout" : PREORDER_MODE ? "Place pre-order" : shopify.connected ? "Secure checkout via Shopify" : "Proceed to checkout"}
          </button>
          {PREORDER_MODE && (
            <p className="mt-3 text-xs text-muted-foreground text-center" data-testid="cart-preorder-note">Pre-order — reserve your pieces now. Production is wrapping up; your order ships within a few days and we'll email you tracking. 🐾</p>
          )}
          {shopify.connected && (
            <p className="mt-3 text-xs text-muted-foreground inline-flex items-center gap-1.5 justify-center w-full" data-testid="cart-shopify-note">
              <Storefront weight="fill" size={13} className="text-secondary" /> Payments &amp; shipping handled by Shopify
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
